// BrancheMultiSelect.tsx — Branchen-Klassifikation als wiederholbare Blöcke.
//
// Ersetzt für die Erfassung die dreifach kaskadierende `BrancheCascadeSelect`:
// dort kostete „eine Gruppe mit fünf Spezialisierungen" fünfmal den vollen Weg
// Branche → Branchengruppe → Spezialisierung. Hier wählt man je Block EINMAL die
// Branche (Ebene 1) und kreuzt danach in EINEM Panel beliebig viele
// Spezialisierungen an — auch über mehrere Gruppen hinweg.
//
// Seit 08/2026 (PO) ist die Maske eine BLOCKLISTE: „man muss mehrere Branchen
// erstellen können und je Branche mehrere Gruppen oder Spezialisierungen".
// Ein Block = eine Ebene 1 + ihre Blätter; „+ weitere Branche" hängt einen
// weiteren an. Ebene 1 ist damit nicht mehr bloßer Anzeigefilter, sondern der
// Schlüssel des Blocks: sein Panel zeigt nur noch deren Gruppen und Blätter.
//
// Nach außen bleibt es bei EINER flachen, geordneten Liste von Ebene-3-Blättern
// (`BrancheAuswahl[]`, erste Zeile = Hauptbranche) — Service, Controller und
// Datenmodell bleiben unberührt. Ein Block ohne Blatt ist deshalb NICHT
// speicherbar (`ebene3` ist NOT NULL); er lebt nur im lokalen Zustand und wird
// sichtbar als unfertig gekennzeichnet.
//
// Warum ein eigenes Panel und nicht `VerantwortlichkeitDropdown` (das Portal,
// Suche, Gruppierung und Chips schon kann): dort trägt jede Zeile und jede
// Gruppenüberschrift ein `bereichIcon` aus dem Verantwortlichkeits-Vokabular —
// für 48 Branchengruppen liefert es nur das Ersatz-Icon. Vor allem fehlt der
// Platz für das „ⓘ" mit der Katalog-Erläuterung, ohne die man 220 teils
// gleichnamige Spezialisierungen nicht auseinanderhält. Die generischen
// Bausteine (`norm`, `moveActive`) werden von dort importiert, das Portal-Muster
// (fixed am Trigger, Auf-/Abklapp-Flip) ist übernommen.
//
// Die Auswahl trägt IMMER die Katalog-id, nie das Label: „Netzbetreiber" und
// „Anlagenbau" gibt es je zweimal in verschiedenen Branchen.
//
// Seit 17.08.2026 (PO): Branche und Spezialisierungen stehen in EINER Zeile
// nebeneinander, das Branchen-Feld startet auf „keine ausgewählt", und die
// Tätigkeitsbeschreibung gilt dem ganzen Betrieb — sie steht am Kontakt, nicht
// mehr je Branchen-Zeile.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { LabeledSelect } from "../formPrimitives";
import { moveActive } from "../../verantwortlichkeit/verantwortlichkeitDropdownLogik";
import {
  useBrancheClassification,
  distinctEbene1,
  findLeaf,
  formatBranchePath,
  istDeaktiviert,
  type BrancheLeaf,
} from "../../../data/brancheClassification";
import {
  auswahlAusBloecken,
  bloeckeAusAuswahl,
  brancheOptionen,
  hauptbrancheInBloecken,
  idsGruppeUmschalten,
  idsUmschalten,
  optionenFiltern,
  optionenFlach,
  optionenGruppieren,
  type BrancheAuswahl,
  type BrancheBlock,
} from "./brancheAuswahlLogik";
import { bestaetigen } from "../../redesign/bestaetigen";
import { alleDokumente, alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/** Höhe des Panels: bevorzugt, Untergrenze bei wenig Platz. */
const PANEL_MAX = 320;
const PANEL_MIN = 200;
const ABSTAND = 4;
const RAND = 12;

const useStyles = makeStyles({
  block: { display: "flex", flexDirection: "column", rowGap: "12px" },
  bloecke: { display: "flex", flexDirection: "column", rowGap: "16px" },
  brancheBlock: { display: "flex", flexDirection: "column", rowGap: "10px" },
  // Die weiteren Branchen brauchen eine sichtbare Grenze, sonst verschwimmen
  // zwei Blöcke zu einer langen Feldreihe.
  brancheBlockWeiter: { paddingTop: "16px", ...shorthands.borderTop("1px", "dashed", REFINED.line) },
  blockLeiste: { display: "flex", alignItems: "baseline", justifyContent: "space-between", columnGap: "10px" },
  blockTitel: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: REFINED.ink3,
  },
  // Branche und Spezialisierungen stehen NEBENeinander (PO 17.08.2026): Sie
  // gehören zu einer Entscheidung, und in den schmalen Anlage-Popups kostete
  // jede zusätzliche Zeile sichtbaren Platz. Unter 420 px bricht es um.
  kopf: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    columnGap: "12px",
    rowGap: "12px",
    alignItems: "end",
  },
  feld: { display: "flex", flexDirection: "column", rowGap: "6px" },
  label: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: REFINED.ink2,
    letterSpacing: "0.02em",
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    width: "100%",
    minHeight: "38px",
    boxSizing: "border-box",
    textAlign: "left",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    color: REFINED.ink,
    cursor: "pointer",
    ":focus-visible": {
      ...shorthands.borderColor(REFINED.aub),
      outlineStyle: "none",
      boxShadow: "0 0 0 3px rgba(125,33,105,0.13)",
    },
  },
  triggerOffen: { ...shorthands.borderColor(REFINED.aub) },
  triggerGesperrt: { backgroundColor: REFINED.aub50, color: REFINED.ink3, cursor: "not-allowed" },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "6px",
    rowGap: "5px",
    flexGrow: 1,
    minWidth: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    maxWidth: "100%",
    ...shorthands.padding("3px", "6px", "3px", "8px"),
    ...shorthands.border("1px", "solid", REFINED.aub100),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  chipText: { overflowX: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chipX: {
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontSize: "0.9rem",
    lineHeight: 1,
    ...shorthands.padding("0", "1px"),
    ":hover": { color: REFINED.aub700 },
  },
  platzhalter: { color: REFINED.ink3, flexGrow: 1 },
  chevron: { color: REFINED.ink3, flexShrink: 0, display: "inline-flex" },

  panel: {
    position: "fixed",
    zIndex: 100000,
    display: "flex",
    flexDirection: "column",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    overflowX: "hidden",
    overflowY: "hidden",
  },
  sucheWrap: {
    ...shorthands.padding("8px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    flexShrink: 0,
  },
  suche: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.padding("7px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("7px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    color: REFINED.ink,
    outlineStyle: "none",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
      boxShadow: "0 0 0 3px rgba(125,33,105,0.13)",
    },
  },
  liste: { overflowY: "auto", overflowX: "hidden", paddingTop: "4px", paddingBottom: "4px", flexGrow: 1 },
  gruppe: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  gruppeBtn: {
    appearance: "none",
    ...shorthands.border("1px", "solid", REFINED.aub100),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "8px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    cursor: "pointer",
    flexShrink: 0,
    ":hover": { backgroundColor: REFINED.aub100 },
  },
  opt: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "9px",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    ...shorthands.padding("7px", "12px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  optAktiv: { backgroundColor: REFINED.aub50 },
  optGewaehlt: { fontWeight: 700, color: REFINED.aub },
  haken: {
    width: "16px",
    height: "16px",
    marginTop: "2px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: REFINED.aub,
  },
  optText: { flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" },
  optHint: {
    display: "block",
    marginTop: "2px",
    fontSize: "0.76rem",
    fontWeight: 400,
    lineHeight: 1.35,
    color: REFINED.ink3,
  },
  infoIcon: { color: REFINED.aub, fontWeight: 800, flexShrink: 0, marginTop: "1px" },
  // Ein deaktivierter Katalog-Eintrag steht nur noch da, weil DIESE Firma ihn
  // trägt — das muss man ihm ansehen, sonst wählt man ihn für die nächste.
  optAlt: {
    marginLeft: "6px",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: REFINED.ink3,
    whiteSpace: "nowrap",
  },
  leer: { ...shorthands.padding("12px"), fontFamily: REFINED.fontUI, fontSize: "0.86rem", color: REFINED.ink3 },

  zeilen: { display: "flex", flexDirection: "column", rowGap: "10px" },
  zeile: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    ...shorthands.padding("10px", "12px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
  },
  zeileKopf: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap" },
  pfad: { flexGrow: 1, minWidth: 0, fontFamily: REFINED.fontUI, fontSize: "0.88rem", fontWeight: 600, color: REFINED.ink },
  badge: {
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.goldBg,
    ...shorthands.border("1px", "solid", REFINED.goldLine),
    color: REFINED.goldInk,
    fontFamily: REFINED.fontUI,
    fontSize: "0.68rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    flexShrink: 0,
  },
  linkBtn: {
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    ...shorthands.padding("0"),
    textDecorationLine: "underline",
    flexShrink: 0,
    ":hover": { color: REFINED.aub700 },
  },
  entfernenBtn: {
    appearance: "none",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.padding("4px", "10px"),
    cursor: "pointer",
    flexShrink: 0,
    ":hover": { backgroundColor: REFINED.errBg },
  },
  weitereBtn: {
    appearance: "none",
    alignSelf: "flex-start",
    ...shorthands.border("1px", "dashed", REFINED.aub200),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("7px", "12px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 700,
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
    ":disabled": { color: REFINED.ink3, cursor: "not-allowed" },
  },
  // Ein Block ohne Spezialisierung ist nicht speicherbar. Er darf trotzdem
  // stehen bleiben, muss aber sagen, dass er so nicht zählt.
  unfertig: {
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.goldLine),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.goldBg,
    color: REFINED.goldInk,
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
  },
  erlaeuterung: { fontFamily: REFINED.fontUI, fontSize: "0.8rem", lineHeight: 1.4, color: REFINED.ink2 },
  hinweis: { fontFamily: REFINED.fontUI, fontSize: "0.8rem", color: REFINED.ink3 },
});

const Chevron: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const Haken: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.2 8.4l3 3 6.6-7" />
  </svg>
);

/**
 * EIN Block: Branche (Ebene 1) + Spezialisierungs-Panel + die gewählten Zeilen.
 * Eigene Komponente, weil Panel-Zustand (offen, Suche, Position) je Block gilt.
 */
const BrancheBlockFeld: React.FC<{
  idBase: string;
  index: number;
  block: BrancheBlock;
  rows: BrancheLeaf[];
  /** Ebene 1 ohne die von den anderen Blöcken belegten Branchen. */
  e1Options: Array<{ value: string; label: string }>;
  loading: boolean;
  required?: boolean;
  /** Erster Block: seine erste Zeile trägt das Hauptbranche-Badge. */
  istErster: boolean;
  /**
   * Beschriftung der Block-Aktion („Branche entfernen" bzw. beim einzigen Block
   * „Branche zurücksetzen"); `null` bietet keine Aktion an.
   */
  entfernenLabel: string | null;
  onEbene1: (ebene1: string) => void;
  onIds: (ids: number[]) => void;
  onHauptbranche: (id: number) => void;
  onEntfernen: () => void;
}> = ({
  idBase,
  index,
  block,
  rows,
  e1Options,
  loading,
  required,
  istErster,
  entfernenLabel,
  onEbene1,
  onIds,
  onHauptbranche,
  onEntfernen,
}) => {
  const s = useStyles();
  const [offen, setOffen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(-1);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const sucheRef = React.useRef<HTMLInputElement>(null);
  const listeRef = React.useRef<HTMLDivElement>(null);

  const optionen = React.useMemo(
    () => brancheOptionen(rows, block.ebene1, block.ids),
    [rows, block.ebene1, block.ids],
  );
  const gruppen = React.useMemo(
    () => optionenGruppieren(optionenFiltern(optionen, query)),
    [optionen, query],
  );
  const sichtbar = React.useMemo(() => optionenFlach(gruppen), [gruppen]);
  const gewaehlteIds = React.useMemo(() => new Set(block.ids.map(String)), [block.ids]);
  const zeilen = block.ids.map((id) => ({ id, leaf: findLeaf(rows, id) }));

  const messen = React.useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const platzUnten = aktivesFenster().innerHeight - r.bottom - ABSTAND - RAND;
    const platzOben = r.top - ABSTAND - RAND;
    const nachOben = platzUnten < PANEL_MIN && platzOben > platzUnten;
    const maxHeight = Math.max(PANEL_MIN, Math.min(PANEL_MAX, nachOben ? platzOben : platzUnten));
    setPos({
      top: nachOben ? Math.max(RAND, r.top - ABSTAND - maxHeight) : r.bottom + ABSTAND,
      left: r.left,
      width: r.width,
      maxHeight,
    });
  }, []);

  React.useEffect(() => {
    if (!offen) return;
    messen();
    const onScroll = () => messen();
    alleFenster.addEventListener("scroll", onScroll, true);
    alleFenster.addEventListener("resize", onScroll);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOffen(false);
    };
    alleDokumente.addEventListener("pointerdown", onDown);
    return () => {
      alleFenster.removeEventListener("scroll", onScroll, true);
      alleFenster.removeEventListener("resize", onScroll);
      alleDokumente.removeEventListener("pointerdown", onDown);
    };
  }, [offen, messen]);

  React.useEffect(() => {
    if (offen && pos) sucheRef.current?.focus();
  }, [offen, pos]);

  // Optionaler Aufruf: scrollIntoView gibt es in jsdom nicht — ein fehlender
  // Scroll darf die Auswahl nie zum Absturz bringen.
  React.useEffect(() => {
    if (active < 0) return;
    listeRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const oeffnen = () => {
    if (loading) return;
    setQuery("");
    setActive(-1);
    setOffen(true);
  };

  const umschalten = (id: number) => onIds(idsUmschalten(block.ids, id));

  const onSucheKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => moveActive(a, e.key === "ArrowDown" ? 1 : -1, sichtbar.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const ziel = active >= 0 ? sichtbar[active] : sichtbar.length === 1 ? sichtbar[0] : undefined;
      if (ziel) umschalten(Number(ziel.value));
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOffen(false);
      setQuery("");
      setActive(-1);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "Backspace" && !query && block.ids.length) {
      e.preventDefault();
      onIds(block.ids.slice(0, -1));
    }
  };

  const spezLabelId = `${idBase}-spez-label-${index}`;

  return (
    <div className={mergeClasses(s.brancheBlock, index > 0 && s.brancheBlockWeiter)}>
      {entfernenLabel && (
        <div className={s.blockLeiste}>
          <span className={s.blockTitel}>{block.ebene1 || "Weitere Branche"}</span>
          <button type="button" className={s.linkBtn} onClick={onEntfernen}>
            {entfernenLabel}
          </button>
        </div>
      )}

      <div className={s.kopf}>
        <LabeledSelect
          label="Branche"
          name={`${idBase}-ebene1-${index}`}
          value={block.ebene1}
          onChange={onEbene1}
          options={e1Options}
          // „alle Branchen" las sich wie eine Auswahl, die schon getroffen ist —
          // gemeint war das Gegenteil: Es ist noch keine gewählt (PO 17.08.2026).
          placeholder={loading ? "Lädt…" : "keine ausgewählt"}
          required={required}
        />

        <div className={s.feld}>
          <span className={s.label} id={spezLabelId}>
            Spezialisierungen
          </span>
          <div
            ref={triggerRef}
            role="combobox"
            aria-expanded={offen}
            aria-haspopup="listbox"
            aria-controls={`${idBase}-panel-${index}`}
            aria-labelledby={spezLabelId}
            aria-disabled={loading || undefined}
            tabIndex={loading ? -1 : 0}
            id={`${idBase}-trigger-${index}`}
            className={mergeClasses(s.trigger, offen && s.triggerOffen, loading && s.triggerGesperrt)}
            onClick={() => (offen ? setOffen(false) : oeffnen())}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                oeffnen();
              }
            }}
          >
            {zeilen.length === 0 ? (
              <span className={s.platzhalter}>
                {loading ? "Lade Katalog …" : "Spezialisierungen wählen (mehrere möglich)"}
              </span>
            ) : (
              <span className={s.chips}>
                {zeilen.map(({ id, leaf }) => (
                  <span key={id} className={s.chip} title={formatBranchePath(leaf)}>
                    <span className={s.chipText}>{leaf?.ebene3 ?? `#${id}`}</span>
                    <button
                      type="button"
                      className={s.chipX}
                      aria-label={`${leaf?.ebene3 ?? id} entfernen`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onIds(idsUmschalten(block.ids, id));
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </span>
            )}
            <span className={s.chevron}>
              <Chevron />
            </span>
          </div>
        </div>
      </div>

      {offen &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            id={`${idBase}-panel-${index}`}
            className={s.panel}
            style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          >
            <div className={s.sucheWrap}>
              <input
                ref={sucheRef}
                className={s.suche}
                value={query}
                placeholder="Suchen … (z. B. Photovoltaik)"
                autoComplete="off"
                aria-label="Spezialisierungen durchsuchen"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(-1);
                }}
                onKeyDown={onSucheKey}
              />
            </div>

            <div className={s.liste} ref={listeRef} role="listbox" aria-multiselectable>
              {sichtbar.length === 0 ? (
                <div className={s.leer}>Keine Spezialisierung gefunden.</div>
              ) : (
                gruppen.map((g) => {
                  const ids = g.options.map((o) => Number(o.value));
                  const alleDrin = ids.every((id) => gewaehlteIds.has(String(id)));
                  return (
                    <div key={g.bereich}>
                      <div className={s.gruppe}>
                        <span>{g.bereich}</span>
                        {/* „Gruppe ohne Spezialisierung" ist nicht speicherbar
                            (ebene3 NOT NULL). Ein Klick hier wählt stattdessen
                            alle Blätter der Gruppe — fachlich dasselbe, ohne
                            Schemaänderung. */}
                        <button
                          type="button"
                          className={s.gruppeBtn}
                          onClick={() => onIds(idsGruppeUmschalten(block.ids, ids))}
                        >
                          {alleDrin ? "Gruppe abwählen" : "ganze Gruppe"}
                        </button>
                      </div>
                      {g.options.map((o) => {
                        const idx = sichtbar.indexOf(o);
                        const gewaehlt = gewaehlteIds.has(o.value);
                        return (
                          <button
                            key={o.value}
                            type="button"
                            role="option"
                            aria-selected={gewaehlt}
                            data-idx={idx}
                            className={mergeClasses(s.opt, idx === active && s.optAktiv, gewaehlt && s.optGewaehlt)}
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => umschalten(Number(o.value))}
                          >
                            <span className={s.haken}>{gewaehlt && <Haken />}</span>
                            <span className={s.optText}>
                              <span>{o.label}</span>
                              {o.deaktiviert && <span className={s.optAlt}>(deaktiviert)</span>}
                              {o.erlaeuterung && <span className={s.optHint}>{o.erlaeuterung}</span>}
                            </span>
                            {o.erlaeuterung && (
                              <span className={s.infoIcon} title={o.erlaeuterung} aria-hidden>
                                ⓘ
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Eine Branche ohne Spezialisierung lässt sich nicht speichern; sie fällt
          beim Neuladen weg. Damit das nicht wie ein Datenverlust aussieht, sagt
          der Block es selbst. */}
      {zeilen.length === 0 && (block.ebene1 !== "" || index > 0) && (
        <div className={s.unfertig}>
          Bitte Spezialisierung wählen — ohne sie wird diese Branche nicht gespeichert.
        </div>
      )}

      {zeilen.length > 0 && (
        <div className={s.zeilen}>
          {zeilen.map(({ id, leaf }, i) => (
            <div key={id} className={s.zeile}>
              <div className={s.zeileKopf}>
                <span className={s.pfad}>{formatBranchePath(leaf) || `Branche #${id}`}</span>
                {istDeaktiviert(leaf) && <span className={s.optAlt}>(deaktiviert)</span>}
                {istErster && i === 0 ? (
                  <span className={s.badge}>Hauptbranche</span>
                ) : (
                  <button type="button" className={s.linkBtn} onClick={() => onHauptbranche(id)}>
                    Als Hauptbranche
                  </button>
                )}
                <button
                  type="button"
                  className={s.entfernenBtn}
                  onClick={() => onIds(idsUmschalten(block.ids, id))}
                >
                  Entfernen
                </button>
              </div>
              {/* Die Tätigkeitsbeschreibung steht NICHT mehr je Branche, sondern
                  einmal für den ganzen Betrieb (PO 17.08.2026) — als Feld
                  „Tätigkeitsbeschreibung" am Kontakt. Wer Handel UND Werkstatt
                  betreibt, beschrieb sonst dasselbe Geschäft zweimal.
                  Bestehende Texte je Zuordnung bleiben in der Datenbank. */}
              {leaf?.erlaeuterung && <div className={s.erlaeuterung}>{leaf.erlaeuterung}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BrancheMultiSelect: React.FC<{
  value: BrancheAuswahl[];
  onChange: (next: BrancheAuswahl[]) => void;
  /** Nur-Lese-Ansicht (fehlendes Schreibrecht). */
  editable?: boolean;
  /** Ebene-1-Feld des ersten Blocks als Pflichtfeld markieren. */
  required?: boolean;
  /** Präfix für die DOM-Ids — mehrere Instanzen je Seite möglich. */
  idBase?: string;
  /**
   * Sparsame Variante fürs Firma-Popup: Mehrere Branchen sind auch dort möglich
   * (PO: „man muss mehrere Branchen erstellen können"), solange es aber bei
   * EINEM Block bleibt, kommt optisch nichts dazu außer „+ weitere Branche" —
   * keine Block-Kopfzeile. Ab dem zweiten Block sieht es aus wie überall.
   */
  kompakt?: boolean;
}> = ({ value, onChange, editable = true, required, idBase = "branche", kompakt = false }) => {
  const s = useStyles();
  const { rows, loading } = useBrancheClassification();

  // Die Blockliste ist der lokale Bearbeitungsstand: ein Block mit gewählter
  // Branche, aber noch ohne Spezialisierung, ist nicht speicherbar (`ebene3` ist
  // NOT NULL) und käme über `value` nie zurück — er muss hier leben.
  const [bloecke, setBloecke] = React.useState<BrancheBlock[]>(() => bloeckeAusAuswahl(rows, value));
  // Signatur der zuletzt selbst gemeldeten Auswahl: nur eine Änderung VON AUSSEN
  // (Laden, Zurücksetzen, Speichern) baut die Blöcke neu auf. Sonst würde jeder
  // eigene onChange die leeren Blöcke gleich wieder einsammeln.
  const gemeldet = React.useRef<string | null>(null);
  const eingang = JSON.stringify(value.map((a) => a.brancheClassId));

  React.useEffect(() => {
    // Ohne Katalog ist die Ebene 1 einer id unbekannt — dann lieber warten,
    // statt alles in einen Block ohne Branche zu werfen.
    if (loading || gemeldet.current === eingang) return;
    gemeldet.current = eingang;
    setBloecke(bloeckeAusAuswahl(rows, value));
  }, [loading, rows, eingang, value]);

  // Die ids ALLER Blöcke: eine Branche, deren Blätter alle deaktiviert sind,
  // muss trotzdem im Feld stehen, solange diese Firma eines davon trägt.
  const gewaehlteIds = React.useMemo(() => bloecke.flatMap((b) => b.ids), [bloecke]);
  const alleE1 = React.useMemo(() => distinctEbene1(rows, gewaehlteIds), [rows, gewaehlteIds]);

  const melden = (next: BrancheBlock[]) => {
    const auswahl = auswahlAusBloecken(next, value);
    gemeldet.current = JSON.stringify(auswahl.map((a) => a.brancheClassId));
    setBloecke(next);
    onChange(auswahl);
  };

  const ersetzen = (i: number, b: BrancheBlock) => melden(bloecke.map((alt, j) => (j === i ? b : alt)));

  const ebene1Setzen = async (i: number, ebene1: string) => {
    const alt = bloecke[i];
    if (alt.ebene1 === ebene1) return;
    // Ohne gewählte Spezialisierung ist nichts zu verlieren — sonst passt die
    // Auswahl nicht mehr zur Überschrift, und das wird gefragt statt getan.
    if (alt.ids.length === 0) {
      setBloecke(bloecke.map((b, j) => (j === i ? { ebene1, ids: [] } : b)));
      return;
    }
    const ok = await bestaetigen({
      titel: "Branche wechseln?",
      text: "Die gewählten Spezialisierungen gehören zur bisherigen Branche und werden dabei entfernt.",
      ja: "Branche wechseln",
      ton: "danger",
    });
    if (!ok) return;
    ersetzen(i, { ebene1, ids: [] });
  };

  const idsSetzen = (i: number, ids: number[]) => {
    const alt = bloecke[i];
    // Ohne gewählte Branche zeigt das Panel den ganzen Katalog — die erste
    // Spezialisierung bestimmt dann die Branche des Blocks.
    const ebene1 = alt.ebene1 || (ids.length > 0 ? (findLeaf(rows, ids[0])?.ebene1 ?? "") : "");
    ersetzen(i, { ebene1, ids });
  };

  const blockEntfernen = (i: number) => {
    const rest = bloecke.filter((_, j) => j !== i);
    melden(rest.length > 0 ? rest : [{ ebene1: "", ids: [] }]);
  };

  // Reiner Maskenzustand: die gespeicherte Auswahl ändert sich dadurch nicht.
  const blockHinzufuegen = () => setBloecke([...bloecke, { ebene1: "", ids: [] }]);

  // Jede Ebene 1 gehört genau einem Block. Sind alle vergeben, hätte ein
  // weiterer Block nichts mehr zu wählen („Keine Spezialisierung gefunden.") —
  // eine Sackgasse, aus der nur „Branche entfernen" herausführt. Also gar nicht
  // erst anbieten.
  const keineFreieBranche =
    alleE1.length > 0 && alleE1.every((e1) => bloecke.some((b) => b.ebene1 === e1));
  const weiterGesperrtGrund = "Alle Branchen des Katalogs sind vergeben — es ist keine Branche mehr frei.";

  /**
   * Beschriftung der Block-Aktion. Beim EINZIGEN Block entfernt sie nichts,
   * sondern LEERT ihn (Ebene 1 zurück auf „keine ausgewählt", Blätter weg) —
   * die Maske zeigt immer mindestens einen Block. Damit die Aktion nicht lügt,
   * heißt sie dann „Branche zurücksetzen". Ist dieser einzige Block schon leer,
   * gäbe es nichts zurückzusetzen: keine Aktion.
   */
  const blockAktion = (b: BrancheBlock): string | null => {
    if (bloecke.length > 1) return "Branche entfernen";
    if (kompakt) return null;
    return b.ebene1 || b.ids.length > 0 ? "Branche zurücksetzen" : null;
  };

  if (!editable) {
    const gruppen = bloeckeAusAuswahl(rows, value).filter((b) => b.ids.length > 0);
    return (
      <div className={s.block}>
        <span className={s.label}>Branche (Klassifikation)</span>
        {gruppen.length === 0 ? (
          <span className={s.hinweis}>Keine Branche klassifiziert.</span>
        ) : (
          <div className={s.bloecke}>
            {gruppen.map((b, bi) => (
              <div
                key={b.ebene1 || `ohne-${bi}`}
                className={mergeClasses(s.brancheBlock, bi > 0 && s.brancheBlockWeiter)}
              >
                <span className={s.blockTitel}>{b.ebene1 || "Ohne Branche"}</span>
                <div className={s.zeilen}>
                  {b.ids.map((id, i) => (
                    <div key={id} className={s.zeile}>
                      <div className={s.zeileKopf}>
                        <span className={s.pfad}>
                          {formatBranchePath(findLeaf(rows, id)) || `Branche #${id}`}
                        </span>
                        {istDeaktiviert(findLeaf(rows, id)) && (
                          <span className={s.optAlt}>(deaktiviert)</span>
                        )}
                        {bi === 0 && i === 0 && <span className={s.badge}>Hauptbranche</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={s.block}>
      <div className={s.bloecke}>
        {bloecke.map((b, i) => {
          // Eine Branche gehört genau einem Block — sonst ständen dieselben
          // Blätter in zwei Blöcken zur Wahl.
          const belegt = new Set(bloecke.filter((_, j) => j !== i).map((x) => x.ebene1).filter(Boolean));
          return (
            <BrancheBlockFeld
              // Position statt Ebene 1 als Schlüssel: die erste Spezialisierung
              // setzt die Branche eines noch leeren Blocks — ein Schlüsselwechsel
              // würde ihn dabei neu aufbauen und das offene Panel zuklappen.
              key={i}
              idBase={idBase}
              index={i}
              block={b}
              rows={b.ebene1 ? rows : rows.filter((r) => !belegt.has(r.ebene1))}
              e1Options={alleE1.filter((e1) => !belegt.has(e1)).map((e1) => ({ value: e1, label: e1 }))}
              loading={loading}
              required={i === 0 ? required : undefined}
              istErster={i === 0}
              entfernenLabel={blockAktion(b)}
              onEbene1={(e1) => ebene1Setzen(i, e1)}
              onIds={(ids) => idsSetzen(i, ids)}
              onHauptbranche={(id) => melden(hauptbrancheInBloecken(bloecke, id))}
              onEntfernen={() => blockEntfernen(i)}
            />
          );
        })}
      </div>

      <button
        type="button"
        className={s.weitereBtn}
        onClick={blockHinzufuegen}
        disabled={loading || keineFreieBranche}
        title={keineFreieBranche ? weiterGesperrtGrund : undefined}
      >
        + weitere Branche
      </button>
      {/* Ein deaktivierter Knopf zeigt in den meisten Browsern kein `title` —
          der Grund steht deshalb auch sichtbar daneben. */}
      {keineFreieBranche && <span className={s.hinweis}>{weiterGesperrtGrund}</span>}
    </div>
  );
};

export default BrancheMultiSelect;
